import { CATEGORY_LABEL } from './final-category';
import { issueLine, stringify } from './report-issues';

import type { FinalRecord, FinalReport, FinalReportRow } from './final-report';
import type { ColumnDef } from '../schemas/bulk-migration';

/**
 * The final report as files: JSON (everything, for whoever will process it)
 * and CSV (one line per client × record, for whoever will open it in a
 * spreadsheet). Pure strings — `download.ts` is what hands them to the browser.
 */

export function finalReportFileName(report: FinalReport, extension: 'json' | 'csv'): string {
    return `${report.header.moduleId}-final-report-${report.header.launch.reportId}.${extension}`;
}

export function toJsonDocument(report: FinalReport): string {
    return JSON.stringify(report, null, 2);
}

/**
 * One field, RFC 4180: quoted when it holds a comma, a quote or a line break,
 * with every quote doubled. A field a spreadsheet would read as a formula
 * (`=`, `+`, `-`, `@`, a tab or a CR first) is prefixed with `'` first: tenant
 * names and Salesforce values come from customer orgs.
 */
export function csvField(value: string): string {
    const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
    return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** A raw module value as text: empty for nothing, JSON for anything structured. */
export function rawText(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return stringify(value);
}

const FIXED_LEAD = [
    'Tenant',
    'Slug',
    'Tenant ID',
    'Sandbox',
    'Category',
    'Reason',
    'Message',
    'Records total',
    'Run ID',
    'Run status',
    'Run discovered',
    'Run migrated',
    'Run failed',
    'Run skipped',
    'Record ID',
    'Record',
] as const;

const RUN_TOTALS = ['discovered', 'migrated', 'failed', 'skipped'] as const;

const FIXED_TAIL = [
    'Not migrated',
    'Record status',
    'V2 ID',
    'Attempts',
    'Migrated at',
    'Warnings',
    'Failure',
] as const;

function recordFields(record: FinalRecord | undefined, columns: readonly ColumnDef[]): string[] {
    const tracking = record?.tracking ?? null;
    return [
        record?.sourceId ?? '',
        record?.title ?? '',
        ...columns.map((column) => rawText(record?.columns[column.key])),
        record?.notMigrated ? issueLine(record.notMigrated) : '',
        tracking?.status ?? '',
        tracking?.v2Id ?? '',
        tracking === null ? '' : String(tracking.attempts),
        tracking?.migratedAt ?? '',
        tracking?.warnings.map(issueLine).join(' | ') ?? '',
        tracking?.failure ? issueLine(tracking.failure) : '',
    ];
}

function rowLines(row: FinalReportRow, columns: readonly ColumnDef[]): string[][] {
    const lead = [
        row.tenant.name,
        row.tenant.slug ?? '',
        row.tenant.tenantId,
        row.tenant.isSandbox ? 'yes' : 'no',
        CATEGORY_LABEL[row.category],
        row.reason ?? '',
        row.message ?? '',
        String(row.recordsTotal),
        row.run?.runId ?? '',
        row.run?.status ?? '',
        ...RUN_TOTALS.map((key) => {
            const value = row.run?.totals?.[key];
            return value === undefined ? '' : String(value);
        }),
    ];
    // A client with no records still gets its line: it is in the report.
    const records: (FinalRecord | undefined)[] =
        row.records.length > 0 ? [...row.records] : [undefined];
    return records.map((record) => [...lead, ...recordFields(record, columns)]);
}

/** `\r\n` between lines, as RFC 4180 has it — what spreadsheets expect. */
export function toCsv(report: FinalReport, columns: readonly ColumnDef[]): string {
    const header = [...FIXED_LEAD, ...columns.map((column) => column.label), ...FIXED_TAIL];
    const lines = [header, ...report.rows.flatMap((row) => rowLines(row, columns))];
    return lines.map((fields) => fields.map(csvField).join(',')).join('\r\n');
}
